/**
 * contactIdentity — Seiteneffekt-freie Helfer für die Dedup-Identität eines
 * Kontakts (Client / ES6).
 *
 * WICHTIG (Phase A / Fundament, 2026-08):
 *   - Dieses Modul ist rein ADDITIV. Es exportiert nur Pure Functions und
 *     schreibt selbst nichts. Existierende Schreibpfade werden NICHT
 *     verändert.
 *   - Die Normalisierung MUSS byte-identisch bleiben zu
 *       src/WorldDash/WorldDbUploader.js  (normalizeEmailForComparison / normalizeBusinessName)
 *       functions/backfillDedupFields.js  (Backfill der historischen Docs)
 *       functions/helpers/contactIdentity.js  (Server-Mirror)
 *     sonst finden die Import-Duplikat-Queries neue Docs nicht.
 *
 * Server-Mirror: functions/helpers/contactIdentity.js (bit-identische Logik).
 */

import { computeDomainDedup, extractEmailDomain, isBusinessDomain } from './dedupNormalize';

export const normalizeEmailForComparison = (email) => {
  if (!email || typeof email !== 'string') return '';
  return email
    .trim()
    .toLowerCase()
    .normalize('NFKC')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

export const normalizeBusinessNameForComparison = (name) => {
  if (!name || typeof name !== 'string') return '';
  return name
    .trim()
    .toLowerCase()
    .replace(/[^\w\s]/g, '')
    .replace(/\s+/g, ' ');
};

export const isValidEmailShape = (email) => {
  if (!email || typeof email !== 'string') return false;
  const norm = email.trim().toLowerCase();
  if (['', '0', 'null', '-', 'undefined'].includes(norm)) return false;
  if (!norm.includes('@')) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(norm);
};

/**
 * Berechnet die drei Dedup-Felder aus einem Contact-Payload.
 * Gibt IMMER Strings zurück (auch leer ''), damit `where(field, 'in', […])`
 * Queries in Firestore sauber indizieren können.
 */
export const computeDedupFields = (contact) => {
  const src = contact || {};
  return {
    lead_email_dedup: isValidEmailShape(src.lead_email)
      ? normalizeEmailForComparison(src.lead_email)
      : '',
    business_name_dedup: normalizeBusinessNameForComparison(src.business_name || ''),
    lead_domain_dedup: computeDomainDedup(src),
  };
};

/**
 * Gibt bei komma-separierter `lead_email` die normalisierte, deduplizierte
 * Liste aller gültig geformten Adressen zurück. Nützlich für Cross-Contact-
 * Checks, die pro einzelner Adresse suchen wollen.
 */
export const extractNormalizedEmails = (rawLeadEmail) => {
  if (!rawLeadEmail || typeof rawLeadEmail !== 'string') return [];
  const seen = new Set();
  const out = [];
  for (const part of rawLeadEmail.split(',')) {
    const trimmed = part.trim();
    if (!isValidEmailShape(trimmed)) continue;
    const norm = normalizeEmailForComparison(trimmed);
    if (!norm || seen.has(norm)) continue;
    seen.add(norm);
    out.push(norm);
  }
  return out;
};

/**
 * Slug für Doc-IDs / Pfade aus einer E-Mail. Kompatibel zur
 * introGuardSlug-Konvention (functions/helpers/introGuard.js).
 */
export const emailSlug = (email) => {
  const norm = normalizeEmailForComparison(email || '');
  if (!norm) return '';
  return norm.replace(/[^a-z0-9]/gi, '_');
};

/**
 * Gibt genau die Dedup-Felder zurück, die sich nach einem Patch ändern
 * würden (idempotent). Doc-ID bleibt unverändert.
 *
 * DEFENSIV (Fix 2026-08-28):
 *   Ein Dedup-Feld wird nur überschrieben, wenn mindestens eines seiner
 *   Source-Felder im `existingDoc` ODER im `contactPatch` explizit vorliegt.
 *   Verhindert stillen Datenverlust bei partial-loaded Firestore-Docs.
 */
export const dedupFieldPatch = (existingDoc, contactPatch) => {
  const existing = existingDoc || {};
  const patchIn = contactPatch || {};
  const merged = { ...existing, ...patchIn };
  const next = computeDedupFields(merged);
  const prev = {
    lead_email_dedup: typeof existing.lead_email_dedup === 'string'
      ? existing.lead_email_dedup : undefined,
    business_name_dedup: typeof existing.business_name_dedup === 'string'
      ? existing.business_name_dedup : undefined,
    lead_domain_dedup: typeof existing.lead_domain_dedup === 'string'
      ? existing.lead_domain_dedup : undefined,
  };

  const has = (obj, k) => Object.prototype.hasOwnProperty.call(obj, k);
  const knowsEmail = has(existing, 'lead_email') || has(patchIn, 'lead_email');
  const knowsName = has(existing, 'business_name') || has(patchIn, 'business_name');
  const knowsWebsite = has(existing, 'lead_website') || has(patchIn, 'lead_website');
  const knowsDomain = knowsEmail || knowsWebsite;

  const patch = {};
  if (knowsEmail && next.lead_email_dedup !== prev.lead_email_dedup) {
    patch.lead_email_dedup = next.lead_email_dedup;
  }
  if (knowsName && next.business_name_dedup !== prev.business_name_dedup) {
    patch.business_name_dedup = next.business_name_dedup;
  }
  if (knowsDomain && next.lead_domain_dedup !== prev.lead_domain_dedup) {
    patch.lead_domain_dedup = next.lead_domain_dedup;
  }
  return patch;
};

export { extractEmailDomain, isBusinessDomain, computeDomainDedup };
